import {
	Component,
	Element,
	Listen,
	Prop,
	Watch,
	getAssetPath,
	h,
} from "@stencil/core";

import mapboxgl from "mapbox-gl";

import { SiteFeatureCollection, SiteProperties } from "@models/site";
import { safeJsonParse } from "@utils/json";
import { addSiteSource, addSitesClickListener } from "@utils/markers";
import MapFullscreenControl from "./controls/map-fullscreen-control";
import MapLegendControl from "./controls/map-legend-control";
import MapSearchControl from "./controls/map-search-control";
import MapToggleControl from "./controls/map-toggle-control";

@Component({
	tag: "mapbox-map",
	styleUrl: "mapbox-map.scss",
})
export class MapboxMap {
	/**
	 * Latitude you want the map to use.
	 * @required
	 * @number
	 */
	@Prop() latitude!: number;
	/**
	 * Longitude you want the map to use.
	 * @required
	 * @number
	 */
	@Prop() longitude!: number;

	/**
	 * Zoom you want the map to use. Default: 9
	 * @optional
	 * @number
	 */
	@Prop() zoom = 9;

	/**
	 * Max zoom level on the map. Default: 20
	 * @optional
	 * @number
	 */
	@Prop() maxZoom = 18;

	/**
	 * Min zoom level on the map. Default: 3
	 * @optional
	 * @number
	 */
	@Prop() minZoom = 3;

	/**
	 * Sites to display on the map
	 * @optional
	 */
	@Prop() sites: SiteFeatureCollection;

	/**
	 * Sites to display on the map
	 * @optional
	 */
	@Prop() siteClickCallback: (site: SiteProperties) => void;

	/**
	 * Shows if the side menu is open
	 * @required
	 * @boolean
	 */
	@Prop() isSideMenuOpen: boolean;

	private map: mapboxgl.Map;

	get computedLatitude() {
		const mapCenter = localStorage.getItem("sitenna-mapCenter");
		const { lat } =
			safeJsonParse<{ lat: number; lng: number }>(mapCenter) || {};
		return lat || this.latitude;
	}

	get computedLongitude() {
		const mapCenter = localStorage.getItem("sitenna-mapCenter");
		const { lng } =
			safeJsonParse<{ lat: number; lng: number }>(mapCenter) || {};
		return lng || this.longitude;
	}

	get computedZoom() {
		const zoomJson = localStorage.getItem("sitenna-mapZoom");
		const zoom = safeJsonParse<number>(zoomJson);
		return zoom || this.zoom;
	}

	// refers to the element itself
	@Element() el: HTMLMapboxMapElement;

	@Watch("sites")
	sitesChangedHandler(
		newSites: SiteFeatureCollection,
		oldSites: SiteFeatureCollection,
	) {
		if (!this.map || newSites === oldSites) return;

		addSiteSource(this.map, newSites);
	}

	@Watch("isSideMenuOpen")
	isSideMenuOpenChangedHandler() {
		if (!this.isSideMenuOpen) {
			const highlightLayer = this.map.getLayer("highlightLayer");
			if (highlightLayer) {
				this.map.removeLayer("highlightLayer");
			}
		}
	}

	// watch window resize to update the side panel width
	@Listen("resize", { target: "window" })
	sidePanelWidth() {
		// query the shadow dom for the side panel
		const shadowRoot = document.querySelector(
			"sitenna-site-navigator",
		).shadowRoot;
		const sidePanel = shadowRoot.querySelector("side-panel");
		const sidePanelWidth = sidePanel.clientWidth || 0;
		const legend = this.el.querySelector(".mapboxgl-ctrl-bottom-left");
		if (!legend) return;
		legend.setAttribute("style", `left: ${sidePanelWidth + 5}px`);
	}

	componentDidRender() {
		// initialize mapbox
		const mapboxApiKey = process.env.MAPBOX_API_KEY;
		const mapboxStyleURL = "mapbox://styles/emmetdel/cl9cvordb004715nl2c48vevd";
		if (mapboxApiKey === undefined) {
			console.error("Mapbox API Key not found.");
			return;
		}

		if (!this.map) {
			mapboxgl.accessToken = mapboxApiKey;
			this.map = new mapboxgl.Map({
				container: this.el.querySelector("#map") as HTMLElement, // container ID
				style: mapboxStyleURL, // style URL
				center: [this.computedLongitude, this.computedLatitude], // starting position [lng, lat]
				zoom: this.computedZoom, // starting zoom
				maxZoom: this.maxZoom,
				minZoom: this.minZoom,
				projection: { name: "mercator" },
			});

			this.map.on("load", this.handleMapOnLoad);

			addSitesClickListener(this.map, this.siteClickCallback);

			this.saveMapParamsLocally();
		}

		// get the initial side panel width
		this.sidePanelWidth();
	}

	private handleMapOnLoad = (
		e: mapboxgl.MapboxEvent<undefined> & mapboxgl.EventData,
	) => {
		e.target.resize();

		// add map toggle control
		this.map.addControl(new MapToggleControl(), "top-right");

		// add fullscreen control
		this.map.addControl(new MapFullscreenControl(), "top-right");

		// add navigation controls to top right of map
		this.map.addControl(
			new mapboxgl.NavigationControl({
				showCompass: true,
				showZoom: true,
				visualizePitch: true,
			}),
			"top-right",
		);

		// add search control
		this.map.addControl(
			new MapSearchControl(this.latitude, this.longitude),
			"top-right",
		);

		// add map legend
		this.map.addControl(new MapLegendControl(), "bottom-left");

		this.overrideMapboxControlsBackgroundImages();
	};

	private saveMapParamsLocally() {
		if (!this.map) return;

		const updateLocalStorage = (latLng: mapboxgl.LngLat, zoom: number) => {
			localStorage.setItem("sitenna-mapCenter", JSON.stringify(latLng));
			localStorage.setItem("sitenna-mapZoom", JSON.stringify(zoom));
		};

		this.map.on("moveend", (e) =>
			updateLocalStorage(e.target.getCenter(), e.target.getZoom()),
		);
		this.map.on("zoomend", (e) =>
			updateLocalStorage(e.target.getCenter(), e.target.getZoom()),
		);
	}

	// method to overide backround images of some of the mapbox controls
	private overrideMapboxControlsBackgroundImages() {
		const zoomIn = this.el.querySelectorAll(".mapboxgl-ctrl-zoom-in");
		const zoomOut = this.el.querySelectorAll(".mapboxgl-ctrl-zoom-out");
		const geoCoder = this.el.querySelectorAll(
			".mapboxgl-ctrl-geocoder--collapsed",
		);

		if (zoomIn.length > 0) {
			// get child span
			const span = zoomIn[0].querySelector("span");
			span.setAttribute(
				"style",
				`background-image: url(${getAssetPath(
					"assets/icon-map-zoomIn.png",
				)}) !important;`,
			);
		}
		if (zoomOut.length > 0) {
			// get child span
			const span = zoomOut[0].querySelector("span");
			span.setAttribute(
				"style",
				`background-image: url(${getAssetPath(
					"assets/icon-map-zoomOut.png",
				)}) !important;`,
			);
		}
		if (geoCoder.length > 0) {
			geoCoder[0].setAttribute(
				"style",
				`background-image: url(${getAssetPath(
					"assets/icon-search-white.svg",
				)})!important; background-repeat: no-repeat; background-position: center;`,
			);
		}
	}

	render() {
		return (
			<div class="container">
				<div id="map" />
			</div>
		);
	}
}
